import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Truck,
  Wrench,
  ShieldCheck,
  Gauge,
  Clock,
  Users,
  Phone,
  Mail,
  CheckCircle2,
  Cog,
  Zap,
  Disc3,
  Snowflake,
  Settings2,
  Activity,
  TrendingUp,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const ACCENT = "#FF6B1A"; // commercial orange — distinct from BMW blue / MotulEVO red / walnut amber

const PILLARS = [
  {
    icon: Users,
    title: "Flotta-szakértelem",
    text:
      "Több debreceni kurír- és kisteher flottát szervízelünk évek óta. Tudjuk, mikor mit kell cserélni egy Ducaton, mielőtt elromlik.",
  },
  {
    icon: Clock,
    title: "Minimális állásidő",
    text:
      "Egyeztetett időpont, gyors átvétel, párhuzamos munkavégzés. Az autója minél előbb visszamehet az útra — ezért időt és pénzt spórolunk.",
  },
  {
    icon: ShieldCheck,
    title: "Garancia és átláthatóság",
    text:
      "Minden javításra és beépített alkatrészre garanciát vállalunk. Munkalap, fotók a hibás alkatrészekről — semmi rejtett tétel.",
  },
  {
    icon: TrendingUp,
    title: "Megelőző karbantartás",
    text:
      "Olajcsere, vezérműlánc, fékek, futómű — időszakos átvizsgálás. Egy 30 ezres átnézés töredéke az árának egy üzemképtelenné vált autónak.",
  },
];

const SERVICES = [
  {
    icon: Cog,
    title: "Motor szervíz",
    text:
      "Olaj- és szűrőcsere, vezérműlánc / vezérműszíj, hűtőfolyadék, vízpumpa. Multijet 2.0 / 2.3 / 3.0 dízel motorokhoz.",
  },
  {
    icon: Settings2,
    title: "Sebességváltó",
    text:
      "Manuális 6 fokozatú váltó és Comfort-Matic robotizált váltó javítása, kuplung csere — Ducato-specifikus szakértelem.",
  },
  {
    icon: Disc3,
    title: "Fék és futómű",
    text:
      "Fékbetét és féktárcsa csere, lengéscsillapítók, gömbcsuklók, kerékcsapágyak. Megrakott, nehéz autókhoz mérezett alkatrészek.",
  },
  {
    icon: Zap,
    title: "Elektromos rendszer",
    text:
      "Hibakód-olvasás, indító, generátor, akku, központi zár, fűtés-szellőztetés. Modern OBD-vel és márkaszintű diagnosztikával.",
  },
  {
    icon: Snowflake,
    title: "Klíma és fűtés",
    text:
      "R134a klímatöltés és -tisztítás, kompresszor javítás, pollenszűrő csere, állófűtés diagnosztika.",
  },
  {
    icon: Activity,
    title: "Műszaki vizsga",
    text:
      "Teljes körű felkészítés a műszaki vizsgára: lámpa, fék, kipufogó, futómű, biztonsági öv. Garancia, hogy átmegy.",
  },
];

const WHY_DUCATO = [
  {
    n: "01",
    title: "A piacvezető Ducato",
    text:
      "A Fiat Ducato Európa legnépszerűbb 3,5 tonnás kisteher autója — a kurír- és kisvállalkozói flotta gerince. Mi minden generációját ismerjük (X230 / X244 / X250 / X290).",
  },
  {
    n: "02",
    title: "Tipikus problémák — előre tudjuk",
    text:
      "Vezérműlánc megnyúlás (2.3 Multijet), turbó hiba, kuplung kopás, AGR szelep eltömődés, EGR rendszer hiba. Ezeket nem keressük — látjuk és pontosan tudjuk a megoldást.",
  },
  {
    n: "03",
    title: "Eredeti és minőségi alkatrészek",
    text:
      "OEM és gyári minőségű alkatrészek — flotta árazáson. Drágább alkatrészeket sem előfizetésre, hanem konkrét munkáért adunk be.",
  },
  {
    n: "04",
    title: "Több autó egyszerre",
    text:
      "Ha hét közben több autója is bekerül, együtt ütemezzük a munkát úgy, hogy ne álljon meg a szállítás. Csak az kerül a szervízbe, ami pont rászorul.",
  },
];

const FAQ = [
  {
    q: "Más kisteher típusokat is szervízelnek?",
    a: "Igen — Renault Master, Opel Movano, Peugeot Boxer, Citroën Jumper, Mercedes Sprinter, VW Crafter, Iveco Daily. A Fiat Ducato a fő szakterületünk, de minden 3,5 tonnás kisteher típushoz felszerelve vagyunk.",
  },
  {
    q: "Mit jelent a flotta-szervíz?",
    a: "Hosszútávú együttműködés cégekkel, ahol több autót egyszerre kezelünk: nyilvántartást vezetünk a futásteljesítményről és a karbantartásokról, automatikusan értesítjük az olajcserék idején, kedvezményes flotta-órabért adunk.",
  },
  {
    q: "Vizsgáztatást is intéznek?",
    a: "A műszaki vizsgára teljes körű felkészítést végzünk, és a vizsga lebonyolításában is segítünk. A vizsga előtti hiányosságokat előre kijavítjuk, így első körben átmegy az autó.",
  },
  {
    q: "Vezérműlánc cseréhez mennyi idő kell?",
    a: "Egy 2.3 Multijet vezérműlánc-csere átlag 1 munkanap — délelőtt behoz, másnap reggel mehet az útra. Komplikált esetben (kopott szelepek, károsodás) akár 2 nap, de ezt előre jelezzük.",
  },
  {
    q: "Mennyibe kerül egy átlagos olajcsere?",
    a: "Az ár függ a típustól és az olaj mennyiségétől (Ducato 2.3 = ~6,5 l). Hívjon vagy küldjön üzenetet — pontos kalkulációt adunk, és flotta esetén kedvezményt biztosítunk.",
  },
];

export default function KisteherauPage() {
  useDocumentMeta({
    title:
      "Kisteher / Fiat Ducato szervíz Debrecen — Flotta-karbantartás | Autó-Szerelem",
    description:
      "Kisteherautó és flotta-szervíz Debrecenben. Fiat Ducato szakértelem (X230/X244/X250/X290), Multijet motor, vezérműlánc, kuplung, fékek, klíma. Renault Master, Mercedes Sprinter, VW Crafter, Iveco Daily is. Minimális állásidő. Időpontfoglalás: +36 30 863 7476.",
    path: "/kisteher-szerviz",
  });

  React.useEffect(() => {
    const id = "kisteher-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Kisteher és flotta szervíz",
        name: "Fiat Ducato és kisteher autó szervíz Debrecenben",
        provider: {
          "@type": "AutoRepair",
          name: "Autó-Szerelem — Séra Autószervíz",
          telephone: "+36-30-863-7476",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Vadvirág utca 15/D — 3. kapu",
            addressLocality: "Debrecen",
            postalCode: "4031",
            addressCountry: "HU",
          },
        },
        areaServed: { "@type": "City", name: "Debrecen" },
        url: "https://autoszerelem.hu/kisteher-szerviz",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A]"
      data-testid="kisteher-page"
    >
      <Navbar />

      {/* HERO */}
      <section
        className="relative pt-24 sm:pt-28 isolate overflow-hidden"
        data-testid="kisteher-hero-section"
      >
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse at 80% 20%, ${ACCENT}40 0%, rgba(10,10,10,0) 55%), radial-gradient(ellipse at 15% 75%, rgba(180,80,0,0.18) 0%, rgba(10,10,10,0) 50%), #0A0A0A`,
            }}
          />
          {/* Industrial diagonal pattern */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 28px), repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 28px)",
            }}
          />
          <Truck
            className="absolute -right-12 -top-8 w-[36rem] h-[36rem] text-white/[0.04] hidden lg:block"
            strokeWidth={0.6}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-16 sm:pt-6 sm:pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-6 transition-colors"
            data-testid="kisteher-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.72rem] font-bold uppercase tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Flotta-szervíz / Debrecen
            </span>
          </div>

          <div
            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur"
            style={{ fontFamily: "Barlow Condensed" }}
          >
            <span
              className="text-[0.65rem] font-black uppercase tracking-[0.28em] text-white"
            >
              Fiat Ducato szakértelem
            </span>
            <span className="w-1 h-1 bg-white/30" />
            <span className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-400">
              X230 · X244 · X250 · X290
            </span>
          </div>

          <h1
            className="font-black uppercase leading-[0.92] tracking-[-0.01em] text-white max-w-5xl"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2rem, 6.5vw, 6rem)",
            }}
            data-testid="kisteher-headline"
          >
            Kisteher és flotta-szervíz —
            <br />
            <span style={{ color: ACCENT }}>Ducato szakember</span>
            <br />
            Debrecenben.
          </h1>

          <p
            className="mt-8 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed"
            data-testid="kisteher-subheadline"
          >
            Több debreceni{" "}
            <span className="text-white font-semibold">kurír- és
            kisvállalkozói flotta</span>{" "}
            mögött állunk évek óta. Fiat Ducato, Renault Master, Mercedes
            Sprinter, VW Crafter — minden 3,5 tonnás kisteher típushoz
            felkészülve. <span className="text-white font-semibold">Minimális
            állásidő</span>, átlátható munkavégzés, garancia.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="kisteher-cta-booking"
            >
              Időpontfoglalás <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="kisteher-cta-phone"
            >
              <Phone className="w-4 h-4" /> +36 30 863 7476
            </a>
          </div>

          {/* Quick facts strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { k: "10+ flotta", v: "Hosszútávú partner" },
              { k: "Min. állásidő", v: "Gyors átfutás" },
              { k: "OEM alkatrész", v: "Eredeti minőség" },
              { k: "Vizsga", v: "Felkészítés + intézés" },
            ].map((s, i) => (
              <div
                key={s.v}
                className="bg-[#0d0d0d] px-5 py-5"
                data-testid={`kisteher-fact-${i}`}
              >
                <div
                  className="text-xl sm:text-2xl font-black uppercase text-white tracking-tight"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.k}
                </div>
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US — Pillars */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="kisteher-pillars-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Miért válasszon minket?</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Egy flottát
                <br />
                <span style={{ color: ACCENT }}>nem amatőrre bíz.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              4 dolog, amiben máshogy dolgozunk, mint egy átlagos szervíz —
              különösen flotta-tulajdonosoknak.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-[#0d0d0d] hover:bg-[#121212] transition-colors p-7"
                  data-testid={`kisteher-pillar-${p.title}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-6 border"
                    style={{
                      backgroundColor: `${ACCENT}10`,
                      borderColor: `${ACCENT}40`,
                    }}
                  >
                    <Icon
                      className="w-6 h-6"
                      style={{ color: ACCENT }}
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="kisteher-services-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Szolgáltatásaink</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Minden, ami a kisteher
                <br />
                <span style={{ color: ACCENT }}>üzemben tartásához kell.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="service-card flex flex-col"
                  data-testid={`kisteher-service-${i}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center border"
                      style={{
                        backgroundColor: `${ACCENT}10`,
                        borderColor: `${ACCENT}40`,
                      }}
                    >
                      <Icon
                        className="w-6 h-6"
                        style={{ color: ACCENT }}
                        strokeWidth={1.5}
                      />
                    </div>
                    <span
                      className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-500"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      0{i + 1}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-bold uppercase text-white leading-tight mb-3"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {s.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY DUCATO — Expertise narrative */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="kisteher-ducato-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Ducato szakértelem</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
              >
                Amit a Ducatókról
                <br />
                <span style={{ color: ACCENT }}>évek alatt megtanultunk.</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              4 dolog, amiért érdemes minket választania, ha Ducato-flottát
              üzemeltet — minden generációt és tipikus hibát ismerünk.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {WHY_DUCATO.map((w) => (
              <div
                key={w.n}
                className="bg-[#0d0d0d] p-8 sm:p-10"
                data-testid={`kisteher-why-${w.n}`}
              >
                <div
                  className="text-5xl font-black tracking-tight"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  {w.n}
                </div>
                <h3
                  className="mt-3 text-xl sm:text-2xl font-bold uppercase text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {w.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                  {w.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING NOTE */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="kisteher-pricing-note"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Wrench
            className="w-10 h-10 mx-auto mb-6"
            style={{ color: ACCENT }}
            strokeWidth={1.4}
          />
          <h3
            className="font-black uppercase text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
            }}
          >
            Flotta-árazás és kedvezmény.
          </h3>
          <p className="mt-5 text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            Egyedi árazás típus és munka szerint. Hosszútávú flotta-szerződés
            esetén kedvezményes órabért és előnyt biztosítunk a sürgős
            munkáknál. Hívjon vagy küldjön üzenetet — egyeztetünk az igényeiről.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="kisteher-pricing-cta-booking"
            >
              Ajánlatkérés <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="kisteher-pricing-cta-phone"
            >
              <Phone className="w-4 h-4" /> Hívás most
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="kisteher-faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="section-eyebrow mb-4">GYIK</span>
          <h2
            className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95] mb-12"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
          >
            Gyakori kérdések.
          </h2>

          <div className="space-y-px bg-white/5 border border-white/5">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="group bg-[#0d0d0d] hover:bg-[#121212] transition-colors"
                data-testid={`kisteher-faq-${f.q}`}
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                  <span className="text-white font-semibold pr-4">{f.q}</span>
                  <span
                    className="w-7 h-7 flex items-center justify-center border border-white/20 text-lg leading-none transition-transform group-open:rotate-45 shrink-0"
                    style={{ color: ACCENT }}
                  >
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-sm text-zinc-400 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section
        className="relative py-20 border-t border-white/5"
        data-testid="kisteher-cta-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0d0d0d] to-[#0a0a0a] border border-white/10 p-10 sm:p-14 overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 blur-3xl pointer-events-none"
              style={{ backgroundColor: `${ACCENT}40` }}
            />
            <div className="m-stripe absolute top-0 left-0 right-0 h-1" />
            <Truck
              className="absolute -right-10 bottom-[-20px] w-64 h-64 text-white/[0.05] hidden md:block"
              strokeWidth={0.7}
            />

            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <span
                  className="text-[0.7rem] font-bold uppercase tracking-[0.24em]"
                  style={{ fontFamily: "Barlow Condensed", color: ACCENT }}
                >
                  Kíméljük az időt — közösen
                </span>
                <h3
                  className="mt-3 font-black uppercase text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                  }}
                >
                  Flotta? Egy autó?
                  <br />
                  Mindkettőt értünk.
                </h3>
                <p className="mt-4 text-zinc-400 max-w-xl">
                  Kérje ajánlatunkat — több autó esetén kedvezményes
                  flotta-szerződést kötünk, amelyben fix kondíciókkal
                  számolhat egész évben.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
                <Link
                  to="/?service=Teljesk%C3%B6r%C5%B1%20szerviz#foglalas"
                  className="btn-primary inline-flex items-center w-full md:w-auto justify-center"
                  data-testid="kisteher-cta-banner-booking"
                >
                  Időpontot foglalok <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-outline inline-flex items-center gap-2 w-full md:w-auto justify-center"
                  data-testid="kisteher-cta-banner-phone"
                >
                  <Phone className="w-4 h-4" /> Hívás most
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
            <a
              href="tel:+36308637476"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="kisteher-contact-phone"
            >
              <Phone
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Telefon
              </div>
              <div className="text-white font-medium mt-1">+36 30 863 7476</div>
            </a>
            <a
              href="mailto:autoszerelem@gmail.com"
              className="bg-[#0e0e0e] border border-white/5 hover:border-white/20 transition-colors p-5"
              data-testid="kisteher-contact-email"
            >
              <Mail
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                E-mail
              </div>
              <div className="text-white font-medium mt-1">
                autoszerelem@gmail.com
              </div>
            </a>
            <div className="bg-[#0e0e0e] border border-white/5 p-5">
              <CheckCircle2
                className="w-5 h-5 mx-auto mb-2"
                style={{ color: ACCENT }}
                strokeWidth={1.6}
              />
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
                Cím
              </div>
              <div className="text-white font-medium mt-1">
                4031 Debrecen, Vadvirág u. 15/D
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
